// The protocol between this app and the interpreter agent (agent/interpreter/protocol.py).
// Same names on both sides; scripts/check-protocol.mjs fails the checks when they drift.
// The design is ADR 001 (.project-wiki/decisions).

/** Participant attribute: the language a participant speaks and wants to hear. Set in the
 *  token by the token service; the app may change it during the meeting. */
export const LANGUAGE_ATTRIBUTE = 'vm.lang';

/** Participant attribute: how this participant listens to people who speak another language
 *  (ADR 001, the listener rule). Set by the app; the interpreter voices a language only while
 *  someone listens to it in voice mode. Missing means voice. */
export const MODE_ATTRIBUTE = 'vm.mode';

export type ListeningMode = 'voice' | 'subtitles' | 'off';
export const LISTENING_MODES: readonly ListeningMode[] = ['voice', 'subtitles', 'off'];
export const DEFAULT_LISTENING_MODE: ListeningMode = 'voice';

export function isListeningMode(value: unknown): value is ListeningMode {
  return typeof value === 'string' && (LISTENING_MODES as readonly string[]).includes(value);
}

/** Participant attribute set by the meeting API: "host" for the person who started the meeting
 *  (the first token of a room), "guest" for everyone who joined by the link (ADR 005). */
export const ROLE_ATTRIBUTE = 'vm.role';
export const ROLE_HOST = 'host';
export const ROLE_GUEST = 'guest';

/** Participant attribute set by the host: whether the copilot follows the meeting for them
 *  ("on" | "off"; missing means off). */
export const COPILOT_ATTRIBUTE = 'vm.copilot';

/** Text-stream topic: the copilot's state (one JSON document per stream), from the interpreter
 *  to the host only (B-023). */
export const COPILOT_TOPIC = 'vm.copilot';

/** Text-stream topic: subtitles, from the interpreter to everyone. One stream carries the
 *  text of one (segment, role, language); a later stream for the same key replaces it. */
export const SUBTITLES_TOPIC = 'vm.subtitles';

/** Text-stream topic: the meeting notes (one JSON document per stream), from the interpreter. */
export const NOTES_TOPIC = 'vm.notes';

/** Written messages between the people in the room: LiveKit's standard chat topic (useChat). */
export const CHAT_TOPIC = 'lk.chat';

/** Files between the people in the room, as byte streams (sendFile / registerByteStreamHandler). */
export const FILES_TOPIC = 'vm.files';

/** The translation the interpreter sends for a sentence it could not read (translate.UNCLEAR). */
export const UNCLEAR_TRANSLATION = '[?]';

/** Attributes on the interpreter's text streams. */
export const STREAM_ATTRIBUTES = {
  segment: 'vm.segment', // one spoken sentence (segment) of one speaker
  speaker: 'vm.speaker', // identity of the human who spoke
  sourceLanguage: 'vm.src_lang', // the language that was spoken
  language: 'vm.lang', // the language of this stream's text
  role: 'vm.role', // "original" | "translation"
  final: 'vm.final', // "true" once the text will not change
  format: 'vm.format', // notes only: "json"
} as const;

export type SubtitleRole = 'original' | 'translation';

/** Name prefix of the interpreter's translated audio tracks: "vm.interp/<lang>/<speaker>". */
export const INTERPRETATION_TRACK_PREFIX = 'vm.interp/';

export interface InterpretationTrack {
  /** The language the track speaks: listeners with this vm.lang play it. */
  language: string;
  /** Identity of the human whose speech it translates (their original is muted for them). */
  speakerIdentity: string;
}

export function interpretationTrackName({ language, speakerIdentity }: InterpretationTrack): string {
  return `${INTERPRETATION_TRACK_PREFIX}${language}/${speakerIdentity}`;
}

/** The track's language and speaker, or null when the name is not an interpretation track. */
export function parseInterpretationTrackName(name: string): InterpretationTrack | null {
  if (!name.startsWith(INTERPRETATION_TRACK_PREFIX)) return null;
  const rest = name.slice(INTERPRETATION_TRACK_PREFIX.length);
  const slash = rest.indexOf('/');
  if (slash <= 0 || slash === rest.length - 1) return null;
  return { language: rest.slice(0, slash), speakerIdentity: rest.slice(slash + 1) };
}
